import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginFailure, loginSuccess } from '../../../redux/slices/authSlice';
import { Button, Col, Form, Input, Row, Typography, Space, notification } from 'antd';
import { BUTTON, TEXT } from '../../../layout/style';
import AuthRestAPI from '../../../restAPI/AuthRestAPI';
import { decodeToken } from "react-jwt";
import { AESEncryption } from '../../../utils/Aes';

const { Title, Text } = Typography;

export default function OtpVerification() {
  const dispatch = useDispatch();
  const [token, setToken] = useState();
  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [otp, setOtp] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [api, contextHolder] = notification.useNotification();

  const RESEND_WAIT_SECONDS = 120; // 2 minutes
  const openNotificationWithIcon = (type, message, description, placement) => {
    api[type]({
      message: message,
      description: description,
      placement
    });
  };

  const startResendCountdown = () => {
    const endTime = new Date(Date.now() + RESEND_WAIT_SECONDS * 1000);
    localStorage.setItem("otpResendCountdownEnd", endTime.toISOString());
    localStorage.setItem("otpResendCountdownExpired", "false"); // Mark countdown as active
    setResendCountdown(RESEND_WAIT_SECONDS);
  };

  useEffect(() => {
    if (location?.state?.token) {
      const { token } = location.state;
      setToken(token);
    }

    // Check if there's an existing countdown to resume from
    const savedEndTime = localStorage.getItem("otpResendCountdownEnd");
    const countdownExpired = localStorage.getItem("otpResendCountdownExpired");

    // Handle countdown logic based on different scenarios
    if (savedEndTime && countdownExpired !== "true") {
      // There's a saved countdown that hasn't expired - resume it
      const remaining = Math.floor(
        (new Date(savedEndTime).getTime() - Date.now()) / 1000
      );
      if (remaining > 0) {
        setResendCountdown(remaining);
      } else {
        // Countdown has expired - mark as expired and don't start new one
        localStorage.removeItem("otpResendCountdownEnd");
        localStorage.setItem("otpResendCountdownExpired", "true");
        setResendCountdown(0);
      }
    } else if (!savedEndTime && countdownExpired !== "true") {
      // No saved countdown and not previously expired - start fresh
      startResendCountdown();
    } else {
      // Countdown was previously expired - don't start new one
      setResendCountdown(0);
    }

    const timer = setInterval(() => {
      const savedEndTime = localStorage.getItem("otpResendCountdownEnd");
      if (savedEndTime) {
        const remaining = Math.floor(
          (new Date(savedEndTime).getTime() - Date.now()) / 1000
        );
        if (remaining > 0) {
          setResendCountdown(remaining);
        } else {
          localStorage.removeItem("otpResendCountdownEnd");
          localStorage.setItem("otpResendCountdownExpired", "true");
          setResendCountdown(0);
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Clear localStorage when navigating away (not on page refresh)
  useEffect(() => {
    // Set a flag to track if component is mounted
    const componentId = `otpComponent_${Date.now()}`;
    localStorage.setItem("otpComponentMounted", componentId);

    return () => {
      // Use a timeout to distinguish between page refresh and navigation
      setTimeout(() => {
        const currentComponentId = localStorage.getItem("otpComponentMounted");

        // If the component ID is different or missing, it means page refreshed
        // If it's the same, it means we navigated away
        if (currentComponentId === componentId) {
          // Same component ID means navigation away - clear localStorage
          const countdownExpired = localStorage.getItem("otpResendCountdownExpired");
          if (countdownExpired !== "true") {
            localStorage.removeItem("otpResendCountdownEnd");
            localStorage.removeItem("otpResendCountdownExpired");
          }
        }
      }, 50);
    };
  }, []);

  const maskEmail = (email) => {
    if (!email || typeof email !== 'string') {
      return email || '';
    }
    const atIdx = email.indexOf('@');
    let maskedEmail = email;
    if (atIdx > 3) {
      maskedEmail =
        email.slice(0, 3) +
        '*'.repeat(atIdx - 3) +
        email.slice(atIdx);
    }
    return maskedEmail;
  };

  const maskMobile = (phone) => {
    if (!phone || typeof phone !== 'string') {
      return phone || '';
    }
    const masked =
      phone && phone.length > 4
        ? phone.slice(0, -4).replace(/./g, 'x') + phone.slice(-4)
        : phone;
    return masked;
  };

  const deliveryMethod = location.state?.method; // sms or email
  const deliveryValue = location.state?.value; // sms or email value
  const maskedInfo = deliveryMethod === 'sms' ? maskMobile(deliveryValue) : maskEmail(deliveryValue);

  const handleVerify = async () => {
    try {
      const formData = {
        "otp": otp,
      };
      await AuthRestAPI.verifyOtpforTwoFactor(formData, null, token)
        .then((res) => {
          // Redirect to dashboard or next step
          if (res.token) {
            const decodedTokenValue = decodeToken(res.token);

            const firstCompanyId = decodedTokenValue.companyData[0].companyId;
            const role = decodedTokenValue.companyData[0].roles;
            const payload = {
              token: res.token,
              userCompany: decodedTokenValue,
              companyId: firstCompanyId,
              role: role
            };

            const encryptedToken = AESEncryption(res.token);
            const encryptedCompanyId = AESEncryption((firstCompanyId).toString());

            localStorage.setItem('token', encryptedToken);
            localStorage.setItem('companyId', encryptedCompanyId);

            dispatch(loginSuccess(payload));
            setIsLoading(false);
            openNotificationWithIcon('success', 'Login successful', `Welcome back!`, 'topRight');

            navigate("/home");
          } else {
            dispatch(loginFailure(res.data));
            openNotificationWithIcon('error', 'Login failed', `${res.data.message}`, 'topRight');
          }

        })
        .catch((err) => {
          if (err.response) {
            openNotificationWithIcon("error", "Two-Factor authentication OTP request failed", `${err.response.data.message}`, "bottomLeft");
          } else {
            openNotificationWithIcon("error", "Two-Factor authentication OTP request failed", `${err.message}`, "bottomLeft");
          }
        });
    } catch (error) {
      console.log('Error in request otp :', error);
    }
  };

  const handleResend = async () => {
    if (resendCountdown > 0) return; // Prevent multiple clicks during countdown

    setOtp(''); // Clear current OTP input

    // Clear expired flag when user manually clicks resend
    localStorage.removeItem("otpResendCountdownExpired");

    try {
      const formData = {
        "method": deliveryMethod,
        "value": deliveryValue
      };
      await AuthRestAPI.requestOtpforTwoFactor(formData, null, token)
        .then((response) => {
          openNotificationWithIcon("success", "Successful", `${response.message}`, "bottomLeft");
          startResendCountdown(); // Start countdown after successful resend
        })
        .catch((err) => {
          if (err.response) {
            openNotificationWithIcon("error", "Two-Factor authentication OTP request failed", `${err.response.data.message}`, "bottomLeft");
          } else {
            openNotificationWithIcon("error", "Two-Factor authentication OTP request failed", `${err.message}`, "bottomLeft");
          }
        });
    } catch (error) {
      console.log('Error in request otp :', error);
    }
  };

  return (
    <div>
      {contextHolder}
      <Row>
        <Col>
          <Title level={2} style={TEXT.heading1}>Enter OTP</Title>
          <Text type="secondary">
            A 6-digit One-Time Password (OTP) has been sent to your registered {deliveryMethod === 'sms' ? 'mobile number' : 'email address'}: <b>{maskedInfo}</b>.
            <br />
            This OTP is valid for 2 minutes. For your security, do not share this code with anyone.
          </Text>
          <br /><br />
        </Col>
      </Row>

      <Row>
        <Col xs={24} sm={18} md={12}>
          <Form form={form} layout="vertical">
            <Form.Item>
              {/* <Input.OTP
                                formatter={str => str.toUpperCase()}
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                maxLength={6}
                                size="large"
                                placeholder="Enter 6-digit OTP"
                            /> */}

              <Input.OTP
                formatter={str => str.toUpperCase()}
                value={otp}
                onChange={setOtp} // <-- just pass setOtp directly
                maxLength={6}
                size="large"
                placeholder="Enter 6-digit OTP"
              />

            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" style={BUTTON.primary} onClick={handleVerify}>
                  Verify OTP
                </Button>
                <Button
                  type="link"
                  disabled={resendCountdown > 0}
                  onClick={handleResend}
                >
                  {resendCountdown > 0
                    ? `Resend OTP (Wait ${resendCountdown} Seconds)`
                    : "Resend OTP"}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Col>
      </Row>
    </div>
  );
}
